<template>
  <div class="modal fade show plan-modal" aria-labelledby="productionPlanTitle">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content plan-modal-content">
        <div class="plan-modal-header">
          <h4 class="mb-0">{{ modalTitle }}</h4>
          <button @click="closeModal" type="button" class="btn-close" aria-label="Close"></button>
        </div>

        <div class="modal-body plan-modal-body">
          <div class="form-section">
            <div class="section-title">Demand</div>
            <div class="row g-1">
              <div v-if="isLineItemLocked" class="col-12 col-lg-6">
                <label class="form-label">Order Item</label>
                <input :value="lockedLineItemLabel" type="text" class="form-control" readonly/>
                <small class="text-muted">Taken from the order tracker</small>
              </div>

              <div v-if="!isLineItemLocked" class="col-12 col-lg-6">
                <label class="form-label">Order Item</label>
                <AsyncSelect
                    placeholder="Select Order Item"
                    v-model="lineItem"
                    :api-service="fetchSalesOrderItemDropdown"
                />
              </div>

              <div class="col-12 col-lg-6">
                <label class="form-label">Customer</label>
                <AsyncSelect
                    placeholder="Select Customer"
                    v-model="customer"
                    :api-service="fetchContactProfiles"
                    :format-label="formatPatientLabel"
                    :additional-query="{type: 'customer'}"
                    :disabled="hasLineItem"
                />
                <small v-if="hasLineItem" class="text-muted">Taken from the selected order item</small>
              </div>
            </div>
          </div>

          <div class="form-section">
            <div class="section-title">What to produce</div>
            <div class="row g-1">
              <div class="col-12 col-lg-4">
                <label class="form-label">Standard Costing <span class="text-danger">*</span></label>
                <AsyncSelect
                    :key="costingSelectKey"
                    placeholder="Select Costing"
                    v-model="costing"
                    :api-service="fetchProductCosting"
                    :additional-query="costingQuery"
                />
                <small v-if="hasLineItem" class="text-muted">Only costings of the selected item</small>
              </div>

              <div class="col-12 col-lg-4">
                <label class="form-label">Product/Item</label>
                <input :value="mainProductName" type="text" class="form-control" readonly placeholder="From costing"/>
              </div>

              <div class="col-12 col-lg-4">
                <label class="form-label">Target Quantity <span class="text-danger">*</span></label>
                <input v-model="formData.qty" type="number" min="0" step="any" class="form-control" placeholder="0"/>
              </div>

              <div class="col-12 col-lg-8">
                <label class="form-label">Plan Title</label>
                <input v-model="formData.title" type="text" class="form-control" placeholder="Production Plan Title"/>
              </div>

              <div class="col-12 col-lg-4">
                <label class="form-label">Status</label>
                <v-select
                    placeholder="Select Status"
                    :options="statusList"
                    v-model="formData.status"
                    label="name"
                    :reduce="name => name.value"
                />
              </div>
            </div>
          </div>

          <div class="form-section">
            <div class="section-title">Schedule &amp; resources</div>
            <div class="row g-1">
              <div class="col-12 col-lg-3">
                <label class="form-label">From Date &amp; Time <span class="text-danger">*</span></label>
                <input v-model="formData.from_date_time" type="datetime-local" class="form-control"/>
              </div>

              <div class="col-12 col-lg-3">
                <label class="form-label">To Date &amp; Time <span class="text-danger">*</span></label>
                <input v-model="formData.to_date_time" type="datetime-local" class="form-control"/>
              </div>

              <div class="col-12 col-lg-3">
                <label class="form-label">Machine/Line</label>
                <AsyncSelect
                    placeholder="Select Machine"
                    v-model="machine"
                    :api-service="fetchProductDropdownList"
                    :format-label="option => option.text"
                    label="text"
                    :additional-query="{product_type: 'fixed_assets', ignore_stock: 1}"
                />
              </div>

              <div class="col-12 col-lg-3">
                <label class="form-label">In-charge Employee</label>
                <AsyncSelect
                    placeholder="Select Employee"
                    v-model="incharge"
                    :api-service="fetchContactProfiles"
                    :format-label="formatPatientLabel"
                    :additional-query="{type: 'employee'}"
                />
              </div>
            </div>
          </div>

          <div class="form-section">
            <div class="section-title">Notes &amp; attachments</div>
            <div class="row g-1">
              <div class="col-12 col-lg-6">
                <label class="form-label">Remarks</label>
                <textarea v-model="formData.remarks" class="form-control" rows="3" placeholder="Remarks"></textarea>
              </div>

              <div class="col-12 col-lg-6 attachment-wrap">
                <label class="form-label">Attachments</label>
                <BlobFileViewer :attachments="formData.attachments"/>
                <FileUpload
                    :is-drag-active="true"
                    button-text="Upload Documents"
                    v-model="formData.attachments"
                >
                </FileUpload>
              </div>
            </div>
          </div>
        </div>

        <div class="plan-modal-footer">
          <button @click="closeModal" class="btn btn-outline-secondary">Cancel</button>
          <button :disabled="loading" @click="submit" class="btn btn-primary">
            <div v-if="loading" class="spinner-border spinner-border-sm text-light me-1" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            Save
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import FileUpload from '@/components/atom/FileUpload'
import BlobFileViewer from '@/components/atom/BlobFileViewer'
import handlePurchase from '@/services/modules/purchase'
import handleContact from '@/services/modules/contact'
import handleDropdowns from '@/services/modules/dropdowns'
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper'
import { handleAttachments, getCurrentDateTime } from '@/services/utils/global'

const props = defineProps({
  editData: {
    type: Object,
    default: null
  },
  lockedOrder: {
    type: Object,
    default: null
  },
  lockedLineItem: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['onSubmit'])

const route = useRoute()
const store = useStore()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const {
  fetchProductCosting,
  fetchSingleProductCosting,
  fetchProductionPlanStatuses,
  storeProductionPlan,
  updateProductionPlan
} = handlePurchase()
const { fetchContactProfiles } = handleContact()
const { fetchProductDropdownList, fetchSalesOrderItemDropdown } = handleDropdowns()
const { formatPatientLabel } = useAsyncDropdownHelper()

const loading = ref(false)
const statusList = ref([])
const costing = ref(null)
const lineItem = ref(null)
const customer = ref(null)
const machine = ref(null)
const incharge = ref(null)
const mainProductName = ref('')

const formData = ref({
  company_id: route.params.companyId,
  product_costing_master_id: null,
  sales_order_general_id: null,
  product_machine_id: null,
  contact_profile_id: null,
  incharge_contact_profile_id: null,
  title: '',
  from_date_time: getCurrentDateTime(),
  to_date_time: getCurrentDateTime(null, { hours: 1 }),
  qty: null,
  status: 'draft',
  remarks: '',
  attachments: []
})

const modalTitle = computed(() => {
  if (props.editData) return 'Edit Production Plan'
  return 'Add Production Plan'
})

const hasLineItem = computed(() => {
  if (!formData.value.sales_order_general_id) return false
  return true
})

const isLineItemLocked = computed(() => {
  if (props.editData) return false
  if (!props.lockedLineItem) return false
  return true
})

const lockedLineItemLabel = computed(() => {
  if (!props.lockedLineItem) return ''
  return `${props.lockedLineItem.product_name} (Order Qty: ${props.lockedLineItem.quantity})`
})

const costingQuery = computed(() => {
  if (!formData.value.sales_order_general_id) return { active_only: 1 }
  return { active_only: 1, sales_order_general_id: formData.value.sales_order_general_id }
})

const costingSelectKey = computed(() => {
  if (!formData.value.sales_order_general_id) return 'costing-all'
  return `costing-${formData.value.sales_order_general_id}`
})

watch(costing, (newValue) => {
  if (!newValue) {
    formData.value.product_costing_master_id = null
    mainProductName.value = ''
    return
  }
  formData.value.product_costing_master_id = newValue.id
  getMainProduct(newValue.id)
})

watch(() => formData.value.from_date_time, (newValue) => {
  if (!newValue) return
  if (formData.value.to_date_time > newValue) return
  formData.value.to_date_time = getCurrentDateTime(newValue, { hours: 1 })
})

watch(lineItem, (newValue) => {
  costing.value = null
  if (!newValue) {
    formData.value.sales_order_general_id = null
    return
  }
  formData.value.sales_order_general_id = newValue.id
  setCustomerFromLineItem(newValue)
  setCostingFromLineItem(newValue)
})

watch(machine, (newValue) => {
  if (!newValue) {
    formData.value.product_machine_id = null
    return
  }
  formData.value.product_machine_id = newValue.id
})

watch(customer, (newValue) => {
  if (!newValue) {
    formData.value.contact_profile_id = null
    return
  }
  formData.value.contact_profile_id = newValue.id
})

watch(incharge, (newValue) => {
  if (!newValue) {
    formData.value.incharge_contact_profile_id = null
    return
  }
  formData.value.incharge_contact_profile_id = newValue.id
})

function setCustomerFromLineItem(newLineItem) {
  if (!newLineItem.contact_profile_id) return
  customer.value = {
    id: newLineItem.contact_profile_id,
    name: newLineItem.contact_name
  }
}

function setCostingFromLineItem(newLineItem) {
  if (!newLineItem.pcm_id) return
  if (Number(newLineItem.pcm_count) > 1) return
  costing.value = {
    id: newLineItem.pcm_id,
    name: newLineItem.pcm_name
  }
}

function getMainProduct(costingId) {
  return fetchSingleProductCosting(costingId, `?company_id=${route.params.companyId}`).then((res) => {
    if (!res.status) return
    const general = res.data.general.find(item => item.type === 'production' && item.product_type === 'main_product')
    if (!general) {
      mainProductName.value = ''
      return showError('Selected costing has no main product.')
    }
    if (!general.product_costingable) {
      mainProductName.value = ''
      return
    }
    mainProductName.value = general.product_costingable.name
  })
}

function getStatusList() {
  return fetchProductionPlanStatuses(`?company_id=${route.params.companyId}&manual_only=1`).then((res) => {
    if (!res.status) return
    statusList.value = res.data
  })
}

function editLineItemLabel(plan) {
  if (!plan.line_item_product_name) return plan.order_number
  return `${plan.order_number} - ${plan.line_item_product_name} (Qty: ${plan.line_item_quantity})`
}

function setEditData() {
  if (!props.editData) return
  const plan = props.editData

  formData.value.product_costing_master_id = plan.product_costing_master_id
  formData.value.sales_order_general_id = plan.sales_order_general_id
  formData.value.product_machine_id = plan.product_machine_id
  formData.value.contact_profile_id = plan.contact_profile_id
  formData.value.incharge_contact_profile_id = plan.incharge_contact_profile_id
  formData.value.title = plan.title
  formData.value.from_date_time = getCurrentDateTime(plan.from_date_time)
  formData.value.to_date_time = getCurrentDateTime(plan.to_date_time)
  formData.value.qty = plan.qty
  formData.value.status = plan.status
  formData.value.remarks = plan.remarks
  formData.value.attachments = plan.attachments || []

  mainProductName.value = plan.product_name || ''

  if (plan.sales_order_general_id) lineItem.value = { id: plan.sales_order_general_id, name: editLineItemLabel(plan) }
  if (plan.product_costing_master_id) costing.value = { id: plan.product_costing_master_id, name: plan.product_costing_name }
  if (plan.product_machine_id) machine.value = { id: plan.product_machine_id, text: plan.machine_name }
  if (plan.contact_profile_id) customer.value = { id: plan.contact_profile_id, name: plan.customer_name }
  if (plan.incharge_contact_profile_id) {
    incharge.value = { id: plan.incharge_contact_profile_id, name: plan.incharge_name }
  }
}

function setLockedLineItem() {
  if (props.editData) return
  if (!props.lockedLineItem) return
  formData.value.sales_order_general_id = props.lockedLineItem.id
  setCustomerFromLockedOrder()
  setCostingFromLineItem(props.lockedLineItem)
}

function setCustomerFromLockedOrder() {
  if (!props.lockedOrder) return
  if (!props.lockedOrder.contact_profile_id) return
  customer.value = {
    id: props.lockedOrder.contact_profile_id,
    name: props.lockedOrder.contact_name
  }
}

function validate() {
  if (!formData.value.product_costing_master_id) return 'Standard costing is required.'
  if (!formData.value.qty || Number(formData.value.qty) <= 0) return 'Target quantity must be greater than zero.'
  if (!formData.value.from_date_time) return 'From date & time is required.'
  if (!formData.value.to_date_time) return 'To date & time is required.'
  if (formData.value.to_date_time <= formData.value.from_date_time) return 'To date must be after from date.'
  return null
}

function buildPayload() {
  const data = new FormData()
  Object.keys(formData.value).forEach((key) => {
    if (key === 'attachments') return
    if (formData.value[key] === null) return
    if (formData.value[key] === '') return
    data.append(key, formData.value[key])
  })
  handleAttachments(data, formData.value.attachments)
  return data
}

function closeModal() {
  store.state.isModalOpen = false
}

async function submit() {
  const errorMessage = validate()
  if (errorMessage) return showError(errorMessage)

  loading.value = true
  const payload = buildPayload()
  const action = getSubmitAction()

  await action(payload).then((res) => {
    if (!res.status) return showError(res.message)
    closeModal()
    emit('onSubmit')
    return showSuccess(res.message)
  }).catch(() => {
    showError('Unable to save the production plan.')
  }).finally(() => {
    loading.value = false
  })
}

function getSubmitAction() {
  if (!props.editData) return storeProductionPlan
  return function (payload) {
    return updateProductionPlan(props.editData.id, payload)
  }
}

onMounted(() => {
  getStatusList()
  setEditData()
  setLockedLineItem()
})
</script>

<style scoped>
.plan-modal {
  display: block;
  background: #1c1d1c66;
}

.plan-modal .modal-dialog {
  max-width: 960px;
  width: calc(100% - 2rem);
  margin-left: auto;
  margin-right: auto;
}

.plan-modal-content {
  border: 0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.18);
}

.plan-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0.85rem;
  border-bottom: 1px solid #eef0f4;
  background: #fbfcfd;
}

.plan-modal-header h4 {
  font-size: 1rem;
}

.plan-modal-body {
  padding: 0.6rem 0.85rem;
  max-height: calc(100vh - 170px);
  overflow-y: auto;
}

.form-section {
  border: 1px solid #eef0f4;
  border-radius: 8px;
  padding: 0.4rem 0.6rem 0.55rem;
  margin-bottom: 0.5rem;
}

.form-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 0.62rem;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.3rem;
}

.plan-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.5rem 0.85rem;
  border-top: 1px solid #eef0f4;
  background: #fbfcfd;
}

.form-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 0.15rem;
}

.plan-modal-body :deep(.form-control),
.plan-modal-body :deep(.vs__dropdown-toggle) {
  min-height: 32px;
  padding-top: 0.2rem;
  padding-bottom: 0.2rem;
  font-size: 0.8rem;
}

.plan-modal-body :deep(textarea.form-control) {
  min-height: 60px;
}

.plan-modal-body :deep(.file-upload) {
  padding: 6px 12px;
  font-size: 0.8rem;
}

.attachment-wrap :deep(.container-fluid) {
  padding: 0;
}

.attachment-wrap :deep(.container-fluid > .row) {
  margin: 0 0 0.35rem;
}

.attachment-wrap :deep(.container-fluid > .row > [class*="col-"]) {
  flex: 0 0 auto;
  width: 100%;
  padding: 0;
}

.attachment-wrap :deep(.files) {
  border: 1px solid #e5e7eb;
  background-color: #fbfcfd;
  border-radius: 6px;
  padding: 6px 8px;
  margin: 0 0 5px;
  flex-wrap: nowrap;
  align-items: center;
}

.attachment-wrap :deep(.files > .col-2) {
  flex: 0 0 auto;
  width: auto;
  padding-right: 8px;
}

.attachment-wrap :deep(.files > .col-10) {
  flex: 1 1 auto;
  width: auto;
  padding: 0;
}

.attachment-wrap :deep(.files .fa-2x) {
  font-size: 1.1em;
  color: #6b7280;
}

.attachment-wrap :deep(.files h5) {
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1.3;
  word-break: break-word;
}

.plan-modal-body :deep(.file-upload + .btn) {
  margin-top: 0.35rem !important;
  padding: 0.25rem 0.6rem;
  font-size: 0.75rem;
}
</style>
